/**
 * Hộp chạy bộ kiểm thử tuân thủ cho một bộ tương thích và hiện kết quả từng ca.
 *
 * Bộ kiểm thử gọi adapter bằng mã thiết bị giả (không phát ra loa thật); kết luận được
 * máy chủ ghi vào `trang_thai_kiem_thu_tuan_thu` — cột quyết định có được gửi lệnh
 * DV002/DV003/DV004/DV009 qua adapter này hay không.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';
import { dienVao } from '../ngon-ngu/vi';
import { viNhaCungCap } from '../ngon-ngu/vi-nha-cung-cap';
import type { BoTuongThich, KetQuaTuanThu } from '../api/nha-cung-cap-api';
import { TheTag } from './MauGiaoDien';

export function HopKiemThuTuanThu({
  btt,
  chay,
  onDong,
}: {
  btt: BoTuongThich;
  chay: (id: string) => Promise<KetQuaTuanThu>;
  onDong: (daChay: boolean) => void;
}): ReactNode {
  const t = viNhaCungCap.btt.tuanThu;
  const idTieuDe = useId();
  const [ketQua, datKetQua] = useState<KetQuaTuanThu | null>(null);
  const [loi, datLoi] = useState<string | null>(null);

  useEffect(() => {
    let conSong = true;
    chay(btt.id).then(
      (kq) => conSong && datKetQua(kq),
      (e: unknown) => conSong && datLoi(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      conSong = false;
    };
  }, [btt.id, chay]);

  return (
    <div className="man-che" role="presentation">
      <div
        className="hop-thoai ncc-tuan-thu"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="kiem-thu-tuan-thu"
      >
        <h2 id={idTieuDe}>{dienVao(t.tieuDe, { ma: btt.maNhaCungCap })}</h2>
        <p className="ncc-dd__mo-ta">{t.moTa}</p>
        {!ketQua && !loi ? (
          <p role="status">{t.dangChay}</p>
        ) : loi ? (
          <p className="ncc-loi" role="alert">
            {dienVao(t.loi, { loi })}
          </p>
        ) : ketQua ? (
          <>
            <p role="status" className="ncc-tuan-thu__ket-luan">
              <TheTag kieu={ketQua.ketLuan === 'dat' ? 'ok' : 'do'}>
                {ketQua.ketLuan === 'dat' ? t.dat : t.khongDat}
              </TheTag>{' '}
              {dienVao(t.tomTat, { dat: ketQua.soDat, tong: ketQua.soCa, sai: ketQua.soKhongDat })}
            </p>
            <div className="ncc-tuan-thu__bang">
              <table className="bang">
                <thead>
                  <tr>
                    <th>{t.ca}</th>
                    <th>{t.noiDung}</th>
                    <th className="bang__giua">{t.ketQua}</th>
                    <th>{t.thucTe}</th>
                  </tr>
                </thead>
                <tbody>
                  {ketQua.ca.map((c) => (
                    <tr key={c.ma} data-ket-qua={c.ketQua}>
                      <td>
                        <strong>{c.ma}</strong>
                        {c.batBuoc ? <span className="bang__dong-phu">{t.batBuoc}</span> : null}
                      </td>
                      <td>
                        {c.ten}
                        <span className="bang__dong-phu">{c.mongDoi}</span>
                      </td>
                      <td className="bang__giua">
                        <TheTag kieu={c.ketQua === 'dat' ? 'ok' : c.ketQua === 'khong_dat' ? 'do' : 'vang'}>
                          {t.giaTri[c.ketQua] ?? c.ketQua}
                        </TheTag>
                      </td>
                      <td className="ncc-tuan-thu__thuc-te">{c.thucTe}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
        <div className="hop-thoai__nut">
          <button
            type="button"
            className="nut"
            disabled={!ketQua && !loi}
            onClick={() => onDong(ketQua !== null)}
          >
            {viNhaCungCap.chung.dong}
          </button>
        </div>
      </div>
    </div>
  );
}
